import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider, styled } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAdminUser } from "@/hooks/use-admin-user";
import { ApprovalDetailDrawer } from "./approval-detail-drawer";
import { ApprovalActionDialog } from "./approval-action-dialog";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": { backgroundColor: theme.palette.action.hover },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

const canDecideStatus = (status) => status === "PENDING";

export function ApprovalActionsPopover({ item }) {
  const { data: adminUser } = useAdminUser();

  // Per the API docs: "Only users with the ROLE_APPROVAL access type can
  // approve/reject an item awaiting approval."
  const isApprover = adminUser?.accessType === "ROLE_APPROVAL";
  const canDecide = isApprover && canDecideStatus(item?.status);

  const [anchorEl, setAnchorEl] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // "approve" | "reject" | null
  const isOpen = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { elevation: 2, sx: { borderRadius: "8px", mt: 0.5 } },
        }}
      >
        <PopoverContent>
          <StyledMenuItem
            onClick={() => {
              handleClose();
              setDetailOpen(true);
            }}
          >
            View
          </StyledMenuItem>

          {canDecide && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  setActionDialog("approve");
                }}
                sx={{ color: "success.main" }}
              >
                Approve
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  setActionDialog("reject");
                }}
                sx={{ color: "error.main" }}
              >
                Reject
              </StyledMenuItem>
            </>
          )}
        </PopoverContent>
      </Popover>

      <ApprovalDetailDrawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        item={item}
      />

      {actionDialog && (
        <ApprovalActionDialog
          open={Boolean(actionDialog)}
          onClose={() => setActionDialog(null)}
          item={item}
          action={actionDialog}
        />
      )}
    </>
  );
}
